import type { ProcessTreeDoc, TreePhase } from './schema.ts';

/**
 * The five phases a job runs through (Jason, 2026-10-03: "organize the tree by
 * the 5 phases so its clear"), as the tree names them: what happens in each,
 * the step that closes it on a job that has it, and how sure the price is once
 * it is closed (the process tree page sets them; `lockIn` carries them into
 * the tree). Every step of the tree has its phase.
 */
export const PHASE_KEYS = ['discovery', 'design', 'development', 'construction', 'closeout'] as const;
export type PhaseKey = (typeof PHASE_KEYS)[number];

/** The tree's phases, in order. */
export function phasesOf(doc: Pick<ProcessTreeDoc, 'phases'>): TreePhase[] {
  return [...doc.phases].sort((a, b) => PHASE_KEYS.indexOf(a.key) - PHASE_KEYS.indexOf(b.key));
}

/** A phase's place in the order every tree reads its phases in (`phasesOf`): 0 for the first. */
export const phaseIndex = (phase: PhaseKey): number => PHASE_KEYS.indexOf(phase);

/**
 * Whether a step is done in an earlier phase than another, as a job's phases read (core `projectPhases`): a task done
 * early (design study 31, slice 4) is, than the step holding it. Either one in no phase: no.
 */
export function earlierPhase(phases: ReadonlyMap<string, PhaseKey>, id: string, than: string): boolean {
  const a = phases.get(id);
  const b = phases.get(than);
  return a !== undefined && b !== undefined && phaseIndex(a) < phaseIndex(b);
}

/** The rule for a task's phase, in words, wherever it is refused (design study 31, slice 4). */
export const TASK_PHASE_RULE = 'A task is done in its step’s phase or an earlier one.';

/** The steps that close a phase, by key (the tree's own phases, `closedBy`): each drawn alone at its phase's foot. */
export function phaseClosers(doc: Pick<ProcessTreeDoc, 'phases'>): Set<string> {
  return new Set(doc.phases.flatMap((p) => (p.closedBy ? [p.closedBy] : [])));
}

/**
 * The phase a step of the tree sits in: what is set on it, else its parent's
 * (a task sits in its step's), else the tree's first. A key the tree does not
 * have (a step of an older tree, still on a job) sits in no phase of it.
 */
export function phaseOfNode(doc: Pick<ProcessTreeDoc, 'nodes' | 'phases'>, key: string): PhaseKey | null {
  const byKey = new Map(doc.nodes.map((n) => [n.key, n]));
  const seen = new Set<string>();
  for (let n = byKey.get(key); n && !seen.has(n.key); n = n.parent !== undefined ? byKey.get(n.parent) : undefined) {
    seen.add(n.key);
    if (n.phase) return n.phase;
    if (n.parent === undefined) break;
  }
  return byKey.has(key) ? (phasesOf(doc)[0]?.key ?? PHASE_KEYS[0]) : null;
}
